import React, { useEffect, useState } from 'react';
import { listSubscriptionGroups, createSubscriptionGroup, listSubscriptionsByGroup, createSubscription, listTerritories, setSubscriptionUsdPrice, listAllSubscriptionPricePoints, createSubscriptionLocalization, getSubscriptionReviewScreenshots, createSubscriptionReviewScreenshot } from '../../services/api';
import axios from 'axios';

export default function MonetizationSubscriptions({ appId }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [subs, setSubs] = useState([]);
  const [subPrices, setSubPrices] = useState({}); // { [subId]: price string }
  const [territories, setTerritories] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [newSub, setNewSub] = useState({ name: '', productId: '', familySharable: false });
  const [usdPrice, setUsdPrice] = useState('');
  const [pricingMsg, setPricingMsg] = useState('');

  // Modal states
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [modalUsdPrice, setModalUsdPrice] = useState('');
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [territoryPrices, setTerritoryPrices] = useState({}); // { [territoryCode]: price }
  const [showTerritoriesView, setShowTerritoriesView] = useState(false);
  const [territoryPricesView, setTerritoryPricesView] = useState({}); // { [territoryCode]: price }
  const [territorySearch, setTerritorySearch] = useState('');
  const [usdPricingEnabled, setUsdPricingEnabled] = useState(false);
  const [localizationStatus, setLocalizationStatus] = useState({}); // { [subId]: 'loading' | 'success' | 'error' }
  const [existingLocalizations, setExistingLocalizations] = useState({}); // { [subId]: boolean }
  const [existingImages, setExistingImages] = useState({}); // { [subId]: boolean }
  const [imageStatus, setImageStatus] = useState({}); // { [subId]: 'loading' | 'success' | 'error' }
  const [availablePrices, setAvailablePrices] = useState([]); // List of available USD prices
  const [showPriceListModal, setShowPriceListModal] = useState(false);
  const [selectedSubForPriceList, setSelectedSubForPriceList] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, t] = await Promise.all([listSubscriptionGroups(appId), listTerritories()]);
      if (g.data?.success) {
        setGroups(g.data.groups);
        if (!selectedGroup && g.data.groups.length > 0) {
          setSelectedGroup(g.data.groups[0].id);
        }
      } else {
        console.warn('[Subscriptions] Failed to get groups', g.data?.error);
      }
      if (t.data?.success) setTerritories(t.data.territories);
    } catch (e) {
      console.error('Failed to load subscriptions data', e);
    } finally { setLoading(false); }
  };

  const loadSubs = async (groupId) => {
    try {
      const res = await listSubscriptionsByGroup(groupId);
      if (res.data?.success) {
        setSubs(res.data.subscriptions);
        // For each subscription, fetch the actual current price (USA) and check localization status and image status
        const priceMap = {};
        const localizationMap = {};
        const imageMap = {};
        await Promise.all((res.data.subscriptions || []).map(async (s) => {
          try {
            const priceRes = await axios.get(`/api/subscriptions/${s.id}/current-price?territory=USA`);
            if (priceRes.data?.success && priceRes.data.customerPrice) {
              priceMap[s.id] = `$${priceRes.data.customerPrice}`;
            }
          } catch {}
          
          // Check if US localization exists
          const hasLocalization = await checkLocalizationExists(s.id);
          localizationMap[s.id] = hasLocalization;
          
          // Check if image exists
          const hasImage = await checkImageExists(s.id);
          imageMap[s.id] = hasImage;
        }));
        setSubPrices(priceMap);
        setExistingLocalizations(localizationMap);
        setExistingImages(imageMap);
      }
    } catch (e) {
      console.error('Failed to list subscriptions', e);
    }
  };

  useEffect(() => { if (appId) load(); }, [appId]);
  useEffect(() => { if (selectedGroup) loadSubs(selectedGroup); else setSubs([]); }, [selectedGroup]);

  const createGroup = async () => {
    if (!groupName) { alert('Enter a reference name'); return; }
    try {
      const res = await createSubscriptionGroup(appId, { referenceName: groupName });
      if (res.data?.success) {
        setGroupName('');
        await load();
      } else {
        alert(res.data?.error || 'Failed to create group');
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const createSub = async () => {
    if (!selectedGroup || !newSub.name || !newSub.productId) { alert('Select group and enter name and product id'); return; }
    try {
      const res = await createSubscription(selectedGroup, { name: newSub.name, productId: newSub.productId, familySharable: newSub.familySharable });
      if (res.data?.success) {
        setNewSub({ name: '', productId: '', familySharable: false });
        await loadSubs(selectedGroup);
      } else {
        const e = res.data?.error;
        alert(typeof e === 'string' ? e : (e?.message || JSON.stringify(e) || 'Failed to create subscription'));
      }
    } catch (e) {
      const err = e.response?.data?.error || e.message || e;
      alert(typeof err === 'string' ? err : JSON.stringify(err));
    }
  };

  const applyUsdPrice = async (subscriptionId) => {
    if (!subscriptionId) { alert('Select a subscription'); return; }
    if (!usdPrice) { alert('Enter USD price (for example, 4.99)'); return; }
    setPricingMsg('Applying USD price to all regions...');
    try {
      const res = await setSubscriptionUsdPrice(subscriptionId, usdPrice);
      if (res.data?.success) {
        const successful = res.data.results?.filter(r => r.success) || [];
        const failed = res.data.results?.filter(r => !r.success) || [];
        let msg = `Price applied to ${successful.length} regions successfully.`;
        if (failed.length > 0) {
          msg += ` Failed for ${failed.length} regions: ${failed.map(f => f.territory).join(', ')}`;
        }
        setPricingMsg(msg);
        // Refresh prices after successful update
        if (successful.length > 0) {
          loadSubs(selectedGroup);
        }
      } else {
        const failed = res.data?.results?.filter(r => !r.success) || [];
        let errorMsg = res.data?.error || 'Unknown error';
        if (failed.length > 0) {
          errorMsg += ` Failed territories: ${failed.map(f => f.territory).join(', ')}`;
        }
        setPricingMsg('Failed: ' + errorMsg);
      }
    } catch (e) {
      setPricingMsg('Failed: ' + (e.response?.data?.error || e.message));
    }
  };

  // Modal functions
  const openPriceModal = async (subscription) => {
    setSelectedSubscription(subscription);
    setSelectedTerritories([]);
    setTerritoryPrices({});
    setPricingMsg(''); // Clear any previous messages
    
    // Load available prices for this subscription
    try {
      const allPricesRes = await listAllSubscriptionPricePoints(subscription.id);
      if (allPricesRes.data?.success && allPricesRes.data.priceGroups) {
        const prices = Object.keys(allPricesRes.data.priceGroups)
          .map(p => parseFloat(p))
          .filter(p => !isNaN(p))
          .sort((a, b) => a - b);
        setAvailablePrices(prices);
        
        if (prices.length === 0) {
          setPricingMsg('⚠️ No price tiers available yet. The subscription may have just been created. Wait a few minutes and try again.');
        }
      } else {
        setAvailablePrices([]);
        setPricingMsg('⚠️ Unable to load price tiers. The subscription may have just been created. You can still try setting a price, or wait a few minutes and reopen this dialog.');
      }
    } catch (e) {
      console.error('Failed to load price tiers', e);
      setAvailablePrices([]);
      setPricingMsg('⚠️ Unable to load price tiers. You can still try setting a price.');
    }
    
    // Load current USD price
    try {
      const priceRes = await axios.get(`/api/subscriptions/${subscription.id}/current-price?territory=USA`);
      if (priceRes.data?.success && priceRes.data.customerPrice) {
        setModalUsdPrice(priceRes.data.customerPrice);
      } else {
        setModalUsdPrice('');
      }
    } catch (e) {
      setModalUsdPrice('');
    }
    
    setShowPriceModal(true);
  };

  const closePriceModal = () => {
    setShowPriceModal(false);
    setSelectedSubscription(null);
    setModalUsdPrice('');
    setSelectedTerritories([]);
    setTerritoryPrices({});
    setTerritorySearch('');
    setUsdPricingEnabled(false);
    setAvailablePrices([]);
  };

  const viewAvailablePrices = async (subscription) => {
    setSelectedSubForPriceList(subscription);
    
    try {
      const allPricesRes = await listAllSubscriptionPricePoints(subscription.id);
      if (allPricesRes.data?.success && allPricesRes.data.priceGroups) {
        const prices = Object.keys(allPricesRes.data.priceGroups)
          .map(p => parseFloat(p))
          .filter(p => !isNaN(p))
          .sort((a, b) => a - b);
        setAvailablePrices(prices);
      }
    } catch (e) {
      console.error('Failed to load price tiers', e);
      setAvailablePrices([]);
    }
    
    setShowPriceListModal(true);
  };

  const closePriceListModal = () => {
    setShowPriceListModal(false);
    setSelectedSubForPriceList(null);
    setAvailablePrices([]);
  };

  const handleTerritorySelect = (territoryCode) => {
    setSelectedTerritories(prev => 
      prev.includes(territoryCode) 
        ? prev.filter(t => t !== territoryCode)
        : [...prev, territoryCode]
    );
  };

  const savePrices = async () => {
    if (!selectedSubscription) return;
    
    setPricingMsg('Setting prices...');
    
    try {
      // If USD pricing is enabled, use the simplified endpoint
      if (usdPricingEnabled && modalUsdPrice) {
        const territoriesToApply = selectedTerritories.length > 0 ? selectedTerritories : null;
        const usdRes = await setSubscriptionUsdPrice(
          selectedSubscription.id, 
          modalUsdPrice, 
          undefined, 
          territoriesToApply
        );
        
        if (usdRes.data?.success) {
          const matchedPrice = usdRes.data.matchedPrice || modalUsdPrice;
          const successCount = usdRes.data.results?.filter(r => r.success).length || 0;
          const failCount = usdRes.data.results?.filter(r => !r.success).length || 0;
          
          let msg = `✓ Price set to $${matchedPrice} for ${successCount} territories`;
          if (failCount > 0) {
            msg += ` (${failCount} failed)`;
          }
          
          setPricingMsg(msg);
          loadSubs(selectedGroup); // Refresh prices
          
          setTimeout(() => {
            closePriceModal();
          }, 2000);
        } else {
          const errorMsg = usdRes.data?.error || 'Unknown error';
          const failed = usdRes.data?.results?.filter(r => !r.success) || [];
          
          let displayMsg = '❌ Failed: ' + errorMsg;
          
          // Add helpful tips based on error type
          if (errorMsg.includes('No price points available') || errorMsg.includes('No USA price points found')) {
            displayMsg += '\n\n💡 Tip: Price points are generated by Apple after subscription creation. Please:\n';
            displayMsg += '1. Wait 5-10 minutes after creating the subscription\n';
            displayMsg += '2. Refresh the page\n';
            displayMsg += '3. Try again\n';
            displayMsg += '4. If issue persists, check App Store Connect to verify subscription exists';
          } else if (failed.length > 0) {
            displayMsg += `\n\nFailed territories: ${failed.slice(0, 5).map(f => f.territory).join(', ')}`;
            if (failed.length > 5) displayMsg += ` and ${failed.length - 5} more`;
          }
          
          setPricingMsg(displayMsg);
        }
      } else if (selectedTerritories.length > 0) {
        // Individual territory pricing (fallback for manual territory selection without USD pricing)
        setPricingMsg('Please enable USD pricing to set prices for territories.');
      } else {
        setPricingMsg('Please enable USD pricing or select territories.');
      }
      
    } catch (e) {
      let errorMsg = '❌ Failed to save prices: ' + (e.response?.data?.error || e.message);
      
      // Add helpful context for common errors
      if (errorMsg.includes('price points') || errorMsg.includes('not found')) {
        errorMsg += '\n\n💡 This usually means the subscription was just created. Wait a few minutes and try again.';
      }
      
      setPricingMsg(errorMsg);
    }
  };

  const viewTerritoryPrices = async (subscription) => {
    setSelectedSubscription(subscription);
    setTerritoryPricesView({});
    
    // Load prices for all territories
    const prices = {};
    for (const territory of territories) {
      try {
        const priceRes = await axios.get(`/api/subscriptions/${subscription.id}/current-price?territory=${territory.id}`);
        if (priceRes.data?.success && priceRes.data.customerPrice) {
          prices[territory.id] = priceRes.data.customerPrice;
        }
      } catch (e) {
        // Ignore errors for individual territories
      }
    }
    
    setTerritoryPricesView(prices);
    setShowTerritoriesView(true);
  };

  const closeTerritoriesView = () => {
    setShowTerritoriesView(false);
    setSelectedSubscription(null);
    setTerritoryPricesView({});
  };

  const createLocalization = async (subscription) => {
    setLocalizationStatus(prev => ({ ...prev, [subscription.id]: 'loading' }));
    try {
      const res = await createSubscriptionLocalization(subscription.id, {
        locale: 'en-US',
        name: subscription.attributes?.name || 'Subscription',
        description: subscription.attributes?.name || 'Subscription app'
      });
      if (res.data?.success) {
        setLocalizationStatus(prev => ({ ...prev, [subscription.id]: 'success' }));
        setExistingLocalizations(prev => ({ ...prev, [subscription.id]: true }));
      } else {
        setLocalizationStatus(prev => ({ ...prev, [subscription.id]: 'error' }));
      }
    } catch (e) {
      setLocalizationStatus(prev => ({ ...prev, [subscription.id]: 'error' }));
    }
  };

  const checkLocalizationExists = async (subscriptionId) => {
    try {
      const res = await axios.get(`/api/subscriptions/${subscriptionId}/localizations`);
      if (res.data?.success && res.data.localizations) {
        const hasUSLocalization = res.data.localizations.some(loc => loc.attributes?.locale === 'en-US');
        setExistingLocalizations(prev => ({ ...prev, [subscriptionId]: hasUSLocalization }));
        return hasUSLocalization;
      }
    } catch (e) {
      console.error('Failed to check localization status', e);
    }
    return false;
  };

  const checkImageExists = async (subscriptionId) => {
    try {
      const res = await getSubscriptionReviewScreenshots(subscriptionId);
      if (res.data?.success && res.data.screenshots) {
        const hasScreenshot = res.data.screenshots.length > 0;
        setExistingImages(prev => ({ ...prev, [subscriptionId]: hasScreenshot }));
        return hasScreenshot;
      }
    } catch (e) {
      console.error('Failed to check screenshot status', e);
    }
    return false;
  };

  const createImage = async (subscription) => {
    setImageStatus(prev => ({ ...prev, [subscription.id]: 'loading' }));
    try {
      // Read the image file from the project
      const imagePath = '/images/subscription-screenshot.png'; // You'll put the PNG here
      const response = await fetch(imagePath);
      const blob = await response.blob();
      
      const result = await createSubscriptionReviewScreenshot(subscription.id, {
        fileName: 'subscription-screenshot.png',
        fileSize: blob.size
      });
      
      if (result.data?.success) {
        setImageStatus(prev => ({ ...prev, [subscription.id]: 'success' }));
        setExistingImages(prev => ({ ...prev, [subscription.id]: true }));
      } else {
        setImageStatus(prev => ({ ...prev, [subscription.id]: 'error' }));
      }
    } catch (e) {
      setImageStatus(prev => ({ ...prev, [subscription.id]: 'error' }));
    }
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0' }}>Subscriptions</h3>

      <div className="form-group">
        <label className="form-label">Create Group</label>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <input className="form-input" placeholder="Reference name" value={groupName} onChange={e => setGroupName(e.target.value)} />
          <button className="btn btn-secondary" onClick={createGroup}>Create Group</button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Subscription Group</label>
        <select className="form-select" value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
          <option value="">Select a group</option>
          {(groups||[]).map(g => (
            <option key={g.id} value={g.id}>{g.attributes?.referenceName || g.id}</option>
          ))}
        </select>
        {(groups||[]).length === 0 && (
          <small className="text-secondary">No groups yet. Create one above.</small>
        )}
      </div>

      {selectedGroup && (
        <div style={{ marginBottom:'1rem' }}>
          <h4>Subscriptions in Group</h4>
          <div className="form-group">
            <label className="form-label">New Subscription Name</label>
            <input className="form-input" placeholder="Monthly Premium" value={newSub.name} onChange={e => setNewSub({ ...newSub, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">New Subscription Product ID</label>
            <input className="form-input" placeholder="com.your.app.sub.monthly" value={newSub.productId} onChange={e => setNewSub({ ...newSub, productId: e.target.value })} />
            <label style={{ display:'block', marginTop:'0.5rem' }}>
              <input type="checkbox" checked={newSub.familySharable} onChange={e => setNewSub({ ...newSub, familySharable: e.target.checked })} /> Family Sharable
            </label>
            <button className="btn btn-primary" style={{ marginTop:'0.5rem' }} onClick={createSub}>Create Subscription</button>
          </div>
          <div className="form-group" style={{ marginTop:'0.5rem' }}>
            <label className="form-label">Apply USD price to this subscription (basic)</label>
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <input className="form-input" placeholder="USD (e.g., 4.99)" value={usdPrice} onChange={e => setUsdPrice(e.target.value)} />
              <button className="btn btn-secondary" onClick={() => applyUsdPrice(subs[0]?.id)} disabled={(subs||[]).length === 0}>Apply to First Subscription</button>
            </div>
            {pricingMsg && <small className="text-secondary">{pricingMsg}</small>}
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Product ID</th>
                <th>Family Sharable</th>
                <th>Current Price (USA)</th>
                <th>Price Management</th>
                <th>Localization</th>
                <th>Review Screenshot</th>
              </tr>
            </thead>
            <tbody>
              {(subs||[]).map(s => (
                <tr key={s.id}>
                  <td>{s.attributes?.productId}</td>
                  <td>{s.attributes?.familySharable ? 'Yes' : 'No'}</td>
                  <td>{subPrices[s.id] || <span className="text-secondary">N/A</span>}</td>
                  <td>
                    <button 
                      className="btn btn-secondary" 
                      style={{ marginRight: '0.5rem' }}
                      onClick={() => openPriceModal(s)}
                      title="Set prices"
                    >
                      Set Price
                    </button>
                    <button 
                      className="btn btn-outline" 
                      style={{ marginRight: '0.5rem' }}
                      onClick={() => viewAvailablePrices(s)}
                      title="View available price tiers"
                    >
                      View Tiers
                    </button>
                    <button 
                      className="btn btn-outline" 
                      onClick={() => viewTerritoryPrices(s)}
                      title="View territory prices"
                    >
                      View Territories
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {existingLocalizations[s.id] && (
                        <button 
                          className="btn btn-success"
                          title="US localization already exists - subscription is sale-ready"
                          disabled
                        >
                          ✓ Valid
                        </button>
                      )}
                      <button 
                        className={`btn ${localizationStatus[s.id] === 'success' ? 'btn-success' : localizationStatus[s.id] === 'error' ? 'btn-danger' : 'btn-outline'}`}
                        onClick={() => createLocalization(s)}
                        disabled={localizationStatus[s.id] === 'loading' || existingLocalizations[s.id]}
                        title={existingLocalizations[s.id] ? 'US localization already exists' : 'Create US localization for sale readiness'}
                      >
                        {localizationStatus[s.id] === 'loading' ? 'Creating...' : 
                         localizationStatus[s.id] === 'success' ? '✓ US Localization' : 
                         localizationStatus[s.id] === 'error' ? '✗ Failed' : 
                         existingLocalizations[s.id] ? 'Already Created' : 'Create US Localization'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {existingImages[s.id] && (
                        <button 
                          className="btn btn-success"
                          title="Review screenshot already exists - subscription is sale-ready"
                          disabled
                        >
                          ✓ Has Screenshot
                        </button>
                      )}
                      <button 
                        className={`btn ${imageStatus[s.id] === 'success' ? 'btn-success' : imageStatus[s.id] === 'error' ? 'btn-danger' : 'btn-outline'}`}
                        onClick={() => createImage(s)}
                        disabled={imageStatus[s.id] === 'loading' || existingImages[s.id]}
                        title={existingImages[s.id] ? 'Review screenshot already exists' : 'Upload review screenshot for sale readiness'}
                      >
                        {imageStatus[s.id] === 'loading' ? 'Uploading...' : 
                         imageStatus[s.id] === 'success' ? '✓ Screenshot Uploaded' : 
                         imageStatus[s.id] === 'error' ? '✗ Failed' : 
                         existingImages[s.id] ? 'Already Uploaded' : 'Upload Screenshot'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(subs||[]).length === 0 && (
                <tr>
                  <td colSpan={6} className="text-secondary" style={{ textAlign: 'center' }}>No subscriptions in this group.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h4>Territories (for pricing)</h4>
        <div style={{ maxHeight: 200, overflowY:'auto', border:'1px solid var(--border-light)', borderRadius:8, padding:'0.5rem' }}>
          {(territories||[]).map(t => (
            <span key={t.id} style={{ display:'inline-block', margin:'0.25rem 0.5rem', padding:'0.25rem 0.5rem', background:'var(--bg-secondary)', borderRadius:6 }}>
              {t.attributes?.currency || ''} • {t.attributes?.code}
            </span>
          ))}
        </div>
      </div>

      {/* Price Setting Modal */}
      {showPriceModal && selectedSubscription && (
        <div className="modal-overlay">
          <div className="modal-content" style={{
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3>Set Prices for {selectedSubscription.attributes?.productId}</h3>
            
            {/* USD Price */}
            <div className="form-group">
              <label className="form-label">
                USD Price {usdPricingEnabled ? 
                  (selectedTerritories.length > 0 ? `(applied to ${selectedTerritories.length} selected territories)` : '(applied to all territories)') : 
                  '(disabled - click Enable to set global USD price)'
                }
                {usdPricingEnabled && (
                  <button 
                    className="btn btn-outline" 
                    style={{ marginLeft: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0.5rem' }}
                    onClick={() => {
                      setUsdPricingEnabled(false);
                      setModalUsdPrice('');
                    }}
                  >
                    Disable
                  </button>
                )}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {usdPricingEnabled ? (
                  availablePrices.length > 0 ? (
                    <select
                      className="form-select"
                      value={modalUsdPrice}
                      onChange={e => setModalUsdPrice(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a price tier...</option>
                      {availablePrices.map(price => (
                        <option key={price} value={price}>${price}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      placeholder="e.g., 4.99"
                      value={modalUsdPrice}
                      onChange={e => setModalUsdPrice(e.target.value)}
                      style={{ flex: 1 }}
                    />
                  )
                ) : (
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    placeholder="e.g., 4.99"
                    value={modalUsdPrice}
                    onChange={e => setModalUsdPrice(e.target.value)}
                    disabled={!usdPricingEnabled}
                    style={{ flex: 1 }}
                  />
                )}
                {!usdPricingEnabled && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setUsdPricingEnabled(true)}
                  >
                    Enable USD Pricing
                  </button>
                )}
              </div>
              {usdPricingEnabled && modalUsdPrice && (
                <small className="text-secondary">
                  Setting ${modalUsdPrice} - {selectedTerritories.length > 0 ? `will apply to ${selectedTerritories.length} selected territories` : 'will apply to ALL territories automatically'}
                </small>
              )}
              {usdPricingEnabled && availablePrices.length > 0 && (
                <small className="text-primary" style={{ display: 'block', marginTop: '0.25rem' }}>
                  💡 Select from available price tiers for best compatibility
                </small>
              )}
            </div>

            {/* Territory Selector - optional for limiting which territories to update */}
            <div className="form-group">
              <label className="form-label">
                Limit to Specific Territories (Optional)
                {selectedTerritories.length > 0 && (
                  <button 
                    className="btn btn-outline" 
                    style={{ marginLeft: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0.5rem' }}
                    onClick={() => setSelectedTerritories([])}
                  >
                    Clear ({selectedTerritories.length} selected)
                  </button>
                )}
              </label>
              <small className="text-secondary" style={{ display: 'block', marginBottom: '0.5rem' }}>
                Leave empty to apply to all territories, or select specific ones below
              </small>
              <input
                className="form-input"
                type="text"
                placeholder="Search territories (e.g., USA, GBR, AUS)..."
                value={territorySearch}
                onChange={e => setTerritorySearch(e.target.value)}
                style={{ marginBottom: '0.5rem' }}
              />
              <div style={{ 
                maxHeight: '150px', 
                overflowY: 'auto', 
                border: '1px solid var(--border-light)', 
                borderRadius: 6, 
                padding: '0.5rem' 
              }}>
                {(territories || [])
                  .filter(t => 
                    territorySearch === '' || 
                    t.id.toLowerCase().includes(territorySearch.toLowerCase()) ||
                    (t.attributes?.currency || '').toLowerCase().includes(territorySearch.toLowerCase())
                  )
                  .map(t => (
                  <label key={t.id} style={{ display: 'block', margin: '0.25rem 0' }}>
                    <input
                      type="checkbox"
                      checked={selectedTerritories.includes(t.id)}
                      onChange={() => handleTerritorySelect(t.id)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    {t.id} ({t.attributes?.currency || 'USD'})
                  </label>
                ))}
                {(territories || [])
                  .filter(t => 
                    territorySearch === '' || 
                    t.id.toLowerCase().includes(territorySearch.toLowerCase()) ||
                    (t.attributes?.currency || '').toLowerCase().includes(territorySearch.toLowerCase())
                  ).length === 0 && territorySearch !== '' && (
                  <div className="text-secondary" style={{ textAlign: 'center', padding: '1rem' }}>
                    No territories found matching "{territorySearch}"
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Status Message */}
            {pricingMsg && (
              <div className={`alert ${pricingMsg.includes('✓') ? 'alert-success' : pricingMsg.includes('❌') ? 'alert-error' : 'alert-warning'}`} style={{ whiteSpace: 'pre-line' }}>
                {pricingMsg}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={savePrices}>
                Save Prices
              </button>
              <button className="btn btn-outline" onClick={closePriceModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Territories View Modal */}
      {showTerritoriesView && selectedSubscription && (
        <div className="modal-overlay">
          <div className="modal-content" style={{
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3>Territory Prices for {selectedSubscription.attributes?.productId}</h3>
            
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto', 
              border: '1px solid var(--border-light)', 
              borderRadius: 6, 
              padding: '0.5rem' 
            }}>
              {Object.entries(territoryPricesView).length > 0 ? (
                Object.entries(territoryPricesView).map(([territory, price]) => (
                  <div key={territory} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '0.5rem',
                    borderBottom: '1px solid var(--border-light)'
                  }}>
                    <span>{territory}</span>
                    <span>${price}</span>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-state-title">No territory prices found</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-outline" onClick={closeTerritoriesView}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price Tiers List Modal */}
      {showPriceListModal && selectedSubForPriceList && (
        <div className="modal-overlay">
          <div className="modal-content" style={{
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3>Available Price Tiers</h3>
            <p className="text-secondary" style={{ marginBottom: '1rem' }}>
              for {selectedSubForPriceList.attributes?.productId}
            </p>
            
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto', 
              border: '1px solid var(--border-light)', 
              borderRadius: 6, 
              padding: '0.5rem' 
            }}>
              {availablePrices.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  {availablePrices.map(price => (
                    <div key={price} style={{ 
                      padding: '0.75rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: 6,
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      ${price}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-title">Loading price tiers...</div>
                </div>
              )}
            </div>

            <div className="alert alert-info" style={{ marginTop: '1rem' }}>
              💡 <strong>Tip:</strong> These are Apple's standard price tiers. When you select a USD price, 
              the system automatically applies equivalent prices to all territories.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-outline" onClick={closePriceListModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
